/* eslint-disable import/first */
/**
 * BaseSelector
 */
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import Tooltip from 'react-bootstrap/lib/Tooltip';
import OverlayTrigger from 'react-bootstrap/lib/OverlayTrigger';
import MenuItem from 'react-bootstrap/lib/MenuItem';
import Input from 'react-bootstrap/lib/FormControl';
import Button from 'react-bootstrap/lib/Button'

class BaseSelector extends Component {
  constructor(props) {
    super(props);
    this.state = {

    }

    /**
     * @private
     * @type {Array<string>}
     * Stores the search matched items for Shift Key operation.
     */
    this.searchMatched = [];
  }

 /**
   * Generates functions for both onKeyUp and onKeyPress. This function 
   * can detect that the enter key event happens inside or outside of 
   * the IME operation and refreshes viz only for the enter key event 
   * outside of the IME operation. 
   *
   * The trick is, if the enter key event happens inside the IME operation,
   * it bypass the keypress event and directly go to the keyup event.
   * If it is outside the IME frame, it goes keypress, then goes to keyup. 
   * Reference: https://garafu.blogspot.com/2015/09/jquery-complete.html
   */
  onKeyHandlerGenerator(callback) {
    let pressed = false;
    const onKeyPressHandler = (ev) => {
      // React's syntheticKeyboardEvent.keyCode returns 0 on key press. Check the native event. 
      pressed = ev.nativeEvent.keyCode === 13;
    };
    const onKeyUpHandler = (ev) => {
      if (ev.nativeEvent.keyCode === 13 && pressed) {
        callback(ev);
      }
      // reset
      pressed = false;
    };
    return {
      onKeyPressHandler: onKeyPressHandler,
      onKeyUpHandler: onKeyUpHandler
    };
  }

  /**
   * API to set focus on the first matched item.
   * @param {Object} container
   * @param {string} filterKey - e.g. nameFilter for column name filter
   * @param {string} matchedItemSelector - query string (class name) for matched items.
   */
  setFocusOnFirstMatchedItem(container, filterKey, matchedItemSelector) {
    try {
      container = container || React.findDOMNode(this);
    } catch (e) {
      //can be ignored.
    }
    if (this.state[filterKey] && this.state[filterKey].length > 0) {
      if (container) {
        let findMatch = false;
        const matchedItems = Array.from(container.querySelectorAll(matchedItemSelector));
        matchedItems.forEach( (item) => {
          if (!findMatch && !item.parentElement.classList.contains('disabled') && !item.querySelector('.no-match') && !item.querySelector('.fa-search') && !item.querySelector('.fa-ban')) {
            findMatch = true;
            item.classList.add('focus');
          } else {
            item.classList.remove('focus');
          }
        });
      }
    } else if (container) {
      const items = container.querySelectorAll('li a.focus');
      Array.from(items).forEach(item => {item.classList.remove('focus')});
      setTimeout(() => {
        // for overlay trigger case, make sure to set focus inside search field.
        let _container = '';
        if (this.props.withOverlayTrigger) {
          _container = document.querySelector('.viz-column-menu.step.tooltip .column-search-field-wrapper');
        } else {
          _container = container.querySelector('.column-search-field-wrapper');
        }
        this.setFocusOnSearchField(_container);
      }, 1)
    }
  }

  /**
   * Event Handler for Enter Key pressed.
   * @param {event} event
   * @param {string} key
   * @param {function} onSelectCallback - callback function after enter key is hit.
   * @param {string} focusOutTarget - to close selector, click outside target with the provided class name
   */
  onEnterKeyPressed(event, key, onSelectCallback, focusOutTarget) {
    if (event.nativeEvent.keyCode === 13) {// Enter key is pressed
      let found = false;
      let count = 0;
      let eventKey = '';
      while (!found && this.refs[key + count]) {
        const element = React.findDOMNode(this.refs[key + count]);
        if (element.querySelector('.focus')) {
          found = true;
          eventKey = this.refs[key + count].props.eventKey;
        } else {
          count++;
        }
      }
      // fallback logic in case event key not found.
      if (!eventKey) {
        const container = React.findDOMNode(this);
        const selectedLink = container.querySelector('.focus');
        if (selectedLink && selectedLink.parentElement) {
          if (this.refs[selectedLink.parentElement.className]) {
            eventKey = this.refs[selectedLink.parentElement.className].props.eventKey;
          }
        }
      }
      if (eventKey) {
        // pass this for the sake of analytics view control so that it can set _isOpen, _isSearchFieldFocused attributes
        onSelectCallback(event, eventKey, this);
        if (focusOutTarget) {
          focusOutTarget.click();
        }
      }
    }
  }

  /**
   * Event Handler for Enter Key pressed.
   * @param {Object} container
   * @param {event} event
   */
  onArrowKeyPressed(container, event, selectTarget) {
    selectTarget = selectTarget || 'ul li a.focus';
    const currentSelect = container.querySelector(selectTarget);
    if (event.nativeEvent.keyCode === 40) { //if down arrow key is pressed
      event.stopPropagation();
      event.preventDefault();
      if (currentSelect && currentSelect.parentElement.nextSibling) {
        currentSelect.classList.remove('focus');
        currentSelect.parentElement.nextSibling.querySelector('a').classList.add('focus');
        container.querySelector('.column-search-field.form-control').focus();
      }
    } else if (event.nativeEvent.keyCode === 38) { // if up arrow key is pressed
      event.stopPropagation();
      event.preventDefault();
      if (currentSelect && currentSelect.parentElement.previousSibling && !currentSelect.parentElement.previousSibling.querySelector('.column-search-field.form-control')) {
        currentSelect.classList.remove('focus');
        currentSelect.parentElement.previousSibling.querySelector('a').classList.add('focus');
        container.querySelector('.column-search-field.form-control').focus();
      }
    }
  }

  /**
   * API to set focus on search input field
   * @param {Object} container (optional)
   * @param {function} callback (optional)
   */
  setFocusOnSearchField(container, callback) {
    setTimeout(() => {
      // wait little bit for the search field to be rendered.
      container = container || ReactDOM.findDOMNode(this.refs['column-search-field']);
      if (container) {
        const inputField = container.querySelector('input');
        if (inputField) {
          this._isSearchFieldFocused = true;
          inputField.focus();
          if (callback) {
            callback(inputField);
          }
        }
      }
    }, 10);
  }

  /**
   * Arrow Key Handler
   * @param {event} event 
   */
  onKeyDown(event) {
    const container = ReactDOM.findDOMNode(this);
    // Call onArrowKeyPressed on SelectorMixin
    this.onArrowKeyPressed(container, event);
  }

  /**
   * Enter Key event handling
   */
  onKeyUpHandler(event) {
    this.onEnterKeyPressed(event, 'col_'/*key*/, this.onDropdownSelect, '.tab-content');
  }


  getInitialState() {
    return { nameFilter: '' };
  }

  getSearchMatched() {
    return this.searchMatched;
  }

  /**
   * API to set column name search key word.
   */
  setNameFilter(event) {
    event.stopPropagation();
    event.nativeEvent.stopImmediatePropagation();
    this.setState({
      nameFilter: event.target.value
    });
  }

  isSelectedItem(valuesArray, item) {
    let result = false;
    valuesArray.some((value) => {
      if (value.value === item.value) {
        result = true;
        return true;
      }
      return false;
    });
    return result;
  }


  // escape special characters for users who expect the input to be normal text
  escapeSpecialCharacterForRegexFunction(exp) {
    const specialCharList = ["\\\\", "\\$", "\\*",  "\\+", "\\.", "\\?", "\\[", "\\]", "\\^", "\\{", "\\}", "\\|", "\\(", "\\)"];
    return specialCharList.reduce((prev, val)=>{
      // replace special characters one by one
      return prev.replace(new RegExp(val, "g"), val);
    }, exp);
  }

  renderNameDropdown(items, _items) {
    const searchKeyWord = this.state.nameFilter;
    let suburbMatchRegex = null;
    if (searchKeyWord !== null && searchKeyWord !== undefined && searchKeyWord !== ''){
      try {
        suburbMatchRegex = new RegExp(this.escapeSpecialCharacterForRegexFunction(searchKeyWord), 'i');        
      } catch (e) {
        console.error(e);
      }
    }
    const res = [];
    const length = items.length;
    // Exclude the items that do not have name attribute
    items.filter((item => {
     return item.value !== undefined && item.value !== null;     
    })).forEach((_item, index) => {
      const key = {"item":_item};
      const isSelected = this.isSelectedItem(_items, _item);
      const isDisabled = this.props.disabledItems && this.props.disabledItems.indexOf(_item.value) > -1;
      const checkbox = (
          <input 
            ref={"checkbox_"+index}
            disabled={isDisabled}
            readOnly={isDisabled}
            onClick={(event) => {
              const isShiftClick = event.shiftKey;
              event.preventDefault();
              event.stopPropagation();
              event.nativeEvent.stopImmediatePropagation();
              setTimeout(()=>{
                // restore shift key event.
                const _event = {};
                _event.shiftKey = isShiftClick;
                this.onDropdownSelect(_event, {"item": _item});
              })
            }}
            style={{"marginRight" : "8px"}} 
            type={this.props.selectionType || "checkbox"} 
            checked={isSelected === true}></input>
      );
      let itemName = _item.displayName || _item.name; // name is string so 0 cannot be there.
      if (suburbMatchRegex){
        const firstMatchIndex = itemName.search(suburbMatchRegex);
        if (firstMatchIndex > -1) {
          const beforeMatch = itemName.slice(0, firstMatchIndex);
          const match = itemName.slice(firstMatchIndex, firstMatchIndex + searchKeyWord.length);
          const afterMatch = itemName.slice(firstMatchIndex + searchKeyWord.length);
          let style ={};
          if (this.props.itemNameStyle) {
            style = this.props.itemNameStyle;
          }
          itemName = (<div style={style} className="column-search-result">{beforeMatch}<strong>{match}</strong>{afterMatch}</div>);
        } else {
          itemName = (<div className="column-search-result no-match">{itemName}</div>);
        }
      }
      if (_item.name.length > 12 ) {
        // Show the popup if the column name is more than 12 #21289.
        itemName = (<OverlayTrigger 
          trigger={"hover"} 
          rootClose={true} 
          placement={"top"}
          overlay={<Tooltip className="lov-item-description">{<div>{_item.name}</div>}</Tooltip>}>
          {<div style={{"width":"100%"}}>{itemName}</div>}
        </OverlayTrigger>);
      }


      let deleteIcon = '';
      if (_item.canRemove) {
        const tooltip = <Tooltip>{this.props.getMessage('dialog.Delete')}</Tooltip>
        deleteIcon = (
          <OverlayTrigger trigger={"hover"} placement={"bottom"} overlay={tooltip} rootClose={true}>
            <i 
              className="fal fa-times" 
              style={{"position": "absolute","right": "10px", "paddingTop": "4px"}} 
              onClick={() => {
                let targetIndex = -1;
                this.props.items.find((item, index) => {
                  if (item.name === _item.name) {
                    targetIndex = index;
                    return true;
                  } else {
                    return false;
                  }
                });
                let isUpdated = false;
                let targetItem = '';
                if (targetIndex > -1) {
                  targetItem = this.props.items[targetIndex]
                  this.props.items.splice(targetIndex, 1);
                  if (this.props.onDeleteCreatedOption) {
                    this.props.onDeleteCreatedOption(this.props.items, targetItem);
                  }
                  isUpdated = true;
                }
                const selectedItemIndex = this.props.selectedItems.indexOf(_item.name);
                if (selectedItemIndex > -1) {
                  this.onDropdownSelect({}, {item: targetItem});
                }
                if (isUpdated) {
                  this.setState({refresh:true});
                }
              }}/>
          </OverlayTrigger>
        );
      }
      let className = '';
      if (isDisabled) {
        className = 'disabled';
      } else if (isSelected) {
        className = 'selected';
      }
      itemName = (<div style={{"transform":"translateY(2px)", "maxWidth": "250px", "overflow": "hidden", "textOverflow": "ellipsis"}}>{itemName}</div>);
      const emptyFunc = ()=>{
        //
      };
      res.push(<MenuItem 
        className={className + " item_" + index}
        onSelect={isDisabled ? emptyFunc : (value, event)=>{this.onDropdownSelect(event, value)}}
        eventKey={key} ref={"item_"+index}>{checkbox} {itemName}{deleteIcon}</MenuItem>);        
      
    });
    
    if ( res && res.length === 0) {
      res.push(<div className="empty-message">{this.props.getMessage('dataframe.No Matching Values')}</div>)
    }
    return res;
  }
  
  clearSearchFilter(){
    this.setState({nameFilter:''});
    setTimeout(() => {
      ReactDOM.findDOMNode(this.refs["column-search-field"]).querySelector('input').focus();
    })
  }

  getSelectedValues() {
    return this.props.selectedItems;
  }
  
  /**
   * API to extract the matched items.
   * 
   *
   * @param   {Array<Object>}  items  [Lov Items]
   * @return  {Array<Object>}         [return description]
   */
  extractedMatched(items) {
    const _items = [];
    if (this.props.selectedItems){
      // iterate selected items and find the match from Lov items.
      this.props.selectedItems.forEach((selectedItem)=>{
        const matchedCol = items.filter((_item)=>{
          return (_item.name === (selectedItem.name || selectedItem)) || 
                 (_item.value === (selectedItem.name || selectedItem));
        })[0];
        if (matchedCol) { // if matched column found, put it in the matched item array.
          _items.push(matchedCol);
        } else { // it means orphan, item add it back to Lov Items as well as selected items.
          const _missingItem = {name:(selectedItem.name||selectedItem)}
          _missingItem.canRemove = true;
          items.push(_missingItem)
          _items.push(_missingItem);
        }
      });
    }
    return _items;
  }

  /**
   * API to handle dropdown select
   *
   * @param   {event}  event     [event description]
   * @param   {Object}  eventKey  [eventKey description]
   *
   * @return  {[type]}            [return description]
   */
  onDropdownSelect(event, eventKey) {
    if (event.target && event.target.classList.contains('fa-times')) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
    this.props.onItemSelect(eventKey, event, this.props.items, this.searchMatched);
  }

  addNewOption() {
    const values = this.props.items.map(item => {
      return item.value;
    });
    if (!values.includes(this.state.nameFilter)) {
      this.props.items.push({name: this.state.nameFilter, value: this.state.nameFilter, canRemove: true});
      this.setState({refresh:true});  
    }
  }

  renderSearchField() {
    const handlers = this.onKeyHandlerGenerator(this.onKeyUpHandler);
    const onKeyUp =  function(){};
    const onKeyPress =  function(){};
    const onKeyDown = function(){};
    const addNewButtonStyle = {"marginRight": "30px", "height":"22px", "marginTop":"5px" };
    if (this.props.lovWidgetType === "list") {
      addNewButtonStyle.marginTop = "2px";
      addNewButtonStyle.marginRight = "10px";
    }
    let addItemLabel = this.props.getMessage("dataframe.Add Item To List");
    addItemLabel = addItemLabel ? addItemLabel.replace("%ITEM%", (this.state.nameFilter !== undefined ? this.state.nameFilter : '')) : '';
    return (
      <MenuItem onDragStart={function(){return false;}} 
        className={this.props.disableSearch ? "hidden" : "column-search-field-wrapper"} 
        disabled={true}><i className={"fal fa-search"}/>
        <Input onDragStart={function(){return false;}} 
          placeholder={this.props.getMessage("dataframe.Type in to Search")} 
          bsSize={"sm"}
          ref={"column-search-field"}
          className={"column-search-field " +  (this.props.lovWidgetType||"")}
          value={this.state.nameFilter !== null && this.state.nameFilter !== undefined ? this.state.nameFilter : ''}
          onMouseDown={function(event){
            event.nativeEvent.stopImmediatePropagation();
          }} 
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          onKeyPress={onKeyPress}
          onChange={this.setNameFilter.bind(this)} 
          type="text">
        </Input>
        <i onClick={this.clearSearchFilter.bind(this)} className={"fal fa-times"}/>
        {this.props.allowCreate && this.state.nameFilter ? <Button onClick={this.addNewOption.bind(this)} className={"add-new-option-button"} style={addNewButtonStyle} bsSize={"xs"}>{addItemLabel}</Button> : ''}
      </MenuItem>);
  }

      
  render() {
    let items = this.props.items;
    const _items = this.extractedMatched(items);
    //if column search key word is entered, filter out columns with the key word.
    if(items && this.state.nameFilter !== undefined && this.state.nameFilter !== null && this.state.nameFilter !== ''){
      items = items.filter((item)=>{
        if (item.name && item.name.toLowerCase && this.state.nameFilter && this.state.nameFilter.toLowerCase) {
          return item.name.toLowerCase().indexOf(this.state.nameFilter.toLowerCase()) > -1;
        } else {
          return true;
        }
      })
    }
    // do not emit change in the middle of rendering but just remembering the search match.
    this.searchMatched = items;
    const listStyle = this.props.listStyle || {};
    const itemDropdown = (
    <div ref="dropdown" className={this.props.className || ""}>
      <ul ref={"search-list"} className={this.props.disableSearch ? "hidden" : "column-list dropdown-menu search open"}>
       {this.renderSearchField()}
      </ul>
      <ul style={listStyle}
      ref={"column-list"} className={"column-list dropdown-menu open"}>
        { this.renderNameDropdown(items, _items) }
      </ul>
    </div>);
    return itemDropdown;
  }
}
export default BaseSelector;
